import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { uniqueId } from 'lodash-es';
import { FilterValue } from '@shared/models';
import { getFormattedAmount } from '@shared/utils';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-filter-amount',
  templateUrl: './filter-amount.component.html',
  imports: [RktIconComponent, RktButtonDirective, FormsModule, ReactiveFormsModule, NgxMaskPipe, NgxMaskDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideNgxMask({ thousandSeparator: ',', decimalMarker: '.' })],
})
export class FilterAmountComponent implements OnChanges, AfterViewInit {
  @HostBinding('class.rkt-filter-item-container') commonClass = true;

  @Input() filterName?: string;

  @Input() selectedValue?: FilterValue;

  @Input() isCollapsable = true;

  @Output() changed = new EventEmitter<string>();

  @Output() errored = new EventEmitter<boolean>();

  @Output() filterToggled = new EventEmitter<void>();

  isCollapsed = true;

  id = uniqueId();

  minAmount = new FormControl();

  maxAmount = new FormControl();

  minAmountError = false;

  maxAmountError = false;

  maxAllowedAmountError = false;

  minAllowedAmountError = false;

  maxAmountEmptyError = false;

  get isError(): boolean {
    return (
      this.minAmountError || this.maxAmountError || this.maxAmountEmptyError || this.maxAllowedAmountError || this.minAllowedAmountError
    );
  }

  get minAmountValue(): number | undefined {
    return this.minAmount.value ? parseFloat(this.minAmount.value) : undefined;
  }

  get maxAmountValue(): number | undefined {
    return this.maxAmount.value ? parseFloat(this.maxAmount.value) : undefined;
  }

  ngAfterViewInit(): void {
    this.isCollapsed = this.isCollapsable && !this.selectedValue;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { currentValue, previousValue } = changes.selectedValue || {};
    if (currentValue === previousValue) {
      return;
    }

    const [min, max] = currentValue?.split('::') || [];

    this.minAmount.setValue(min || undefined);
    this.maxAmount.setValue(max || undefined);
  }

  getFormattedValue(value: string) {
    if (!value) {
      return undefined;
    }
    return getFormattedAmount(parseFloat(value)).formattedValue;
  }

  onInputChange() {
    this.validateAmountValues();

    if (this.minAmount.value || this.maxAmount.value) {
      this.changed.emit(`${this.minAmount.value || ''}::${this.maxAmount.value || ''}`);
    } else {
      this.onResetBtnClick();
    }
  }

  onMinAmountFieldBlur(): void {
    if (!this.minAmount.value) {
      this.minAmount.reset();
    } else {
      this.minAmount.setValue(this.getFormattedValue(this.minAmount.value));
    }
    this.onInputChange();
  }

  onMaxAmountFieldBlur(): void {
    if (!this.maxAmount.value) {
      this.maxAmount.reset();
    } else {
      this.maxAmount.setValue(this.getFormattedValue(this.maxAmount.value));
    }
    this.onInputChange();
  }

  validateAmountValues(): void {
    const minAmount = this.minAmountValue;
    const maxAmount = this.maxAmountValue;

    this.minAmountError = maxAmount ? maxAmount > 0 && minAmount !== 0 && !minAmount : false;
    this.maxAmountError = minAmount && maxAmount ? minAmount > 0 && maxAmount > 0 && minAmount > maxAmount : false;
    this.maxAmountEmptyError = minAmount ? minAmount > 0 && !maxAmount : false;
    this.maxAllowedAmountError = (minAmount && minAmount > 999999) || (maxAmount && maxAmount > 999999) || false;

    this.errored.emit(this.isError);
  }

  onResetBtnClick() {
    this.minAmount.setValue(undefined);
    this.maxAmount.setValue(undefined);
    this.changed.emit();
    this.validateAmountValues();
  }

  toggleFilter() {
    if (!this.isCollapsable) {
      return;
    }

    this.isCollapsed = !this.isCollapsed;
    this.filterToggled.emit();
  }
}
